import { GetStaticPaths, GetStaticProps } from 'next';
import React, { useEffect } from 'react';
import AuctionSummaryWithDetails from '@src/components/auction/auction-summary-with-details';
import { AuctioneerType } from '@src/enums/auctioneer-type-enum';
import CardGrid from '@src/components/card-grid/card-grid';
import { DateTime } from 'luxon';
import { EVENT_LOCATION } from '@src/hooks/useTrackEvent';
import { Filters } from '@src/context/filterContext';
import { GetAuction_auction } from '@src/types/generated/GetAuction';
import LoadingGrid from '@src/components/card-grid/loading-grid';
import PageLayout from '@src/components/page-layout/page-layout';
import Script from 'next/script';
import { getAllAuctioneers } from '@src/services/auctioneers/get-all-auctioneers';
import { getAuction } from '@src/services/auction/get-auction';
import { getPosition } from '@src/context/positionContext';
import makeStyles from '@mui/styles/makeStyles';
import { useSearch } from '@src/components/search/hooks/use-search';

type Props = { auction?: GetAuction_auction; errors?: string };

const styles = makeStyles((theme) => ({
    content: {
        display: 'grid',
        gridGap: 14,
        gridTemplateColumns: '1fr',
        margin: 'auto',
        minHeight: '80vh',
        paddingBottom: theme.spacing(5),
        paddingTop: theme.spacing(5),
        [theme.breakpoints.up('md')]: {
            gridTemplateColumns: '400px 1fr',
        },
    },
}));

const index = ({ auction, errors }: Props) => {
    if (errors) return <div>{errors}</div>;

    const classes = styles();
    const location = getPosition();

    const { isLoading, loadMore, result, search } = useSearch();

    const filters = { auctionSlug: auction.slug, sortBy: 'year', sortDirection: 'desc' } as Filters;

    useEffect(() => {
        if (location?.latitude) {
            void search(filters);
        }
    }, [location?.latitude]);

    let eventAttendanceMode;
    let auctionLocation;

    if (auction.online === 'Online') {
        auctionLocation = {
            '@type': 'VirtualLocation',
            url: auction.biddingUrl,
        };
        eventAttendanceMode = 'https://schema.org/OnlineEventAttendanceMode';
    } else if (auction.online === 'Onsite') {
        auctionLocation = {
            '@type': 'Place',
            name: auction.location,
        };
        eventAttendanceMode = 'https://schema.org/OfflineEventAttendanceMode';
    } else if (auction.online === 'Simulcast') {
        auctionLocation = {
            '@type': 'VirtualLocation',
            url: auction.biddingUrl,
        };
        eventAttendanceMode = 'https://schema.org/MixedEventAttendanceMode';
    }

    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'Event',
        eventAttendanceMode,
        eventStatus: 'https://schema.org/EventScheduled',
        image: [auction.auctioneer.logo],
        location: auctionLocation,
        name: auction.auctioneer?.company,
        organizer: {
            '@type': 'Organization',
            name: auction.auctioneer?.company,
            url: auction.auctioneer?.website,
        },
        startDate: DateTime.fromMillis(Number(auction.date)).toISODate(),
    };

    return (
        <>
            <Script type='application/ld+json'>{JSON.stringify(structuredData)}</Script>
            <PageLayout
                description={auction.description}
                fixed
                image={{
                    alt: auction.auctioneer.slug + '-logo',
                    height: 120,
                    url: auction.auctioneer.logo,
                    width: 120,
                }}
                title={
                    auction.auctioneer.company +
                    "'s Auction - " +
                    DateTime.fromMillis(Number(auction.date)).toFormat('EEE. MMM dd, yyyy')
                }
            >
                <div className={classes.content}>
                    <AuctionSummaryWithDetails auction={auction} />
                    <div>
                        <div style={{ marginBottom: 14 }}>
                            <span style={{ fontWeight: 700, marginRight: 12 }}>Equipment at Auction</span>
                            <span style={{ color: '#0e1c3699' }}>{result.filterTotal} Results</span>
                        </div>
                        {isLoading ? (
                            <LoadingGrid />
                        ) : result.results.length ? (
                            <CardGrid
                                cards={result.results}
                                location={EVENT_LOCATION.AUCTION}
                                loadMoreItems={loadMore}
                                totalResults={result.filterTotal}
                            />
                        ) : (
                            <h2>No Equipment Found.</h2>
                        )}
                    </div>
                </div>
            </PageLayout>
        </>
    );
};

export default index;

export const getStaticPaths: GetStaticPaths = async () => {
    const auctioneers = process.env.VERCEL_ENV === 'production' ? await getAllAuctioneers() : [];

    const paths = [];

    auctioneers
        .filter((auctioneer) => auctioneer.type === AuctioneerType.AUCTIONEER)
        .map((x) => x.auctions.map((a) => paths.push({ params: { auction: a.slug, auctioneer: x.slug } })));

    return { fallback: 'blocking', paths };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
    let props: Props;

    const id = params?.auction as string;

    try {
        console.debug('fetching auction with id', id);
        const auction = await getAuction(id as string);

        console.log(auction);

        if (!auction) {
            return {
                redirect: {
                    destination: `/beta/auctioneer/${params?.auctioneer as string}`,
                    permanent: true,
                    props,
                },
            };
        }

        props = { auction };
    } catch (err) {
        console.error(err);

        props = { errors: err.message };
    }

    return { props, revalidate: 60 };
};
